import { useState, useEffect, useRef, useCallback } from 'react';
import { OTT_PLATFORMS } from '../lib/ottPlatforms';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import { GoldBtn, Spinner } from '../components/UI';
import {
  getAllUsers, getAllReports, getActiveBans,
  banUser, unbanUser, updateReportStatus,
  getFilmRequests, updateFilmRequestStatus,
  getFilms, insertFilm, deleteFilm, uploadFilmPoster,
  supabase, createAdminBattle, getAllActiveBattles, toggleBattle,
  getBattleSuggestions, updateFilmMeta,
} from '../lib/supabase';
import { getOMDBByTitle } from '../lib/omdb';

const BAN_DURATIONS = [
  { label: '7 Days',    days: 7 },
  { label: '30 Days',   days: 30 },
  { label: '6 Months',  days: 180 },
  { label: '1 Year',    days: 365 },
  { label: 'Permanent', days: 36500 },
];

const GENRE_OPTIONS = [
  'Action','Adventure','Animation','Biography','Comedy','Crime',
  'Documentary','Drama','Fantasy','Horror','Musical','Mystery',
  'Romance','Sci-Fi','Sport','Superhero','Thriller','War','Western',
];

const EMPTY_FORM = {
  title: '', year: '', language: '', poster_emoji: '🎬',
  imdb_rating: '', description: '', genres: [], ott_platforms: [], type: 'movie',
};

// ── SHARED INPUT STYLES ───────────────────────────────────────
const inputStyle = {
  width: '100%', background: 'var(--surface2)', border: '1px solid var(--border2)',
  borderRadius: 6, color: 'var(--text)', padding: '10px 14px', fontSize: 15,
  fontFamily: "'Crimson Text', serif", outline: 'none',
};
const labelStyle = {
  display: 'block', color: 'var(--text-dim)', fontSize: 13,
  marginBottom: 6, letterSpacing: 0.3,
};

function focusGold(e)  { e.target.style.borderColor = 'var(--gold-dim)'; }
function blurBorder(e) { e.target.style.borderColor = 'var(--border2)'; }

// ── STAT CARD ─────────────────────────────────────────────────
function StatCard({ icon, label, value, accent }) {
  return (
    <div style={{
      background: 'var(--surface)', border: `1px solid ${accent ? 'rgba(212,175,55,0.2)' : 'var(--border)'}`,
      borderRadius: 12, padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 16,
    }}>
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, color: accent ? 'var(--gold)' : 'var(--text)', fontWeight: 700, lineHeight: 1 }}>{value}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

// ── BAN MODAL ─────────────────────────────────────────────────
function BanModal({ target, onClose, onBan }) {
  const [duration, setDuration] = useState(30);
  const [reason, setReason] = useState('');
  if (!target) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(4,4,6,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(6px)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 14, padding: 28, minWidth: 360, maxWidth: 440 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#C0392B', fontSize: 20, marginBottom: 6 }}>Ban Account</h3>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 20 }}>User: <strong style={{ color: 'var(--text)' }}>@{target.username}</strong></p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...labelStyle, marginBottom: 8 }}>Duration</label>
          {BAN_DURATIONS.map(({ label, days }) => (
            <label key={days} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: duration === days ? 'var(--gold)' : 'var(--text-dim)', fontSize: 15, marginBottom: 6 }}>
              <input type="radio" name="banDur" checked={duration === days} onChange={() => setDuration(days)} style={{ accentColor: 'var(--gold)' }} />
              {label}{days === 36500 && <span style={{ fontSize: 11, color: 'var(--red)', marginLeft: 4 }}>⚠ permanent</span>}
            </label>
          ))}
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Reason (optional)</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for ban…"
            style={{ ...inputStyle, height: 72, resize: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <GoldBtn danger onClick={() => onBan(target, duration, reason)}>Apply Ban</GoldBtn>
          <GoldBtn outline onClick={onClose}>Cancel</GoldBtn>
        </div>
      </div>
    </div>
  );
}

// ── FILM FORM (shared by Add + Edit) ─────────────────────────
// FIX 1: This component is NOT remounted when switching tabs —
//         form state lives here and persists across tab switches.
// FIX 2: image uses objectFit:'contain' so nothing is cropped.
function FilmForm({ initial, onSaved, onCancel, isEdit }) {
  const fileRef = useRef();
  const DRAFT_KEY = isEdit ? null : 'critiqr_film_draft'; // only persist Add form, not Edit

  // Restore draft from localStorage on first load (Add form only)
  const [form, setForm] = useState(() => {
    if (isEdit || !DRAFT_KEY) return initial || EMPTY_FORM;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : EMPTY_FORM;
    } catch { return EMPTY_FORM; }
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initial?.poster_url || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Save to localStorage whenever form changes (Add form only)
  useEffect(() => {
    if (!DRAFT_KEY) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {}
  }, [form, DRAFT_KEY]);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const toggleGenre = g => setForm(p => ({
    ...p,
    genres: p.genres.includes(g) ? p.genres.filter(x => x !== g) : [...p.genres, g],
  }));

  const toggleOTT = id => setForm(p => ({
    ...p,
    ott_platforms: (p.ott_platforms || []).includes(id)
      ? (p.ott_platforms || []).filter(x => x !== id)
      : [...(p.ott_platforms || []), id],
  }));

  const handleImagePick = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!form.title.trim()) return setError('Film title is required.');
    if (!form.language.trim()) return setError('Language is required.');
    if (form.genres.length === 0) return setError('Select at least one genre.');
    setUploading(true);
    try {
      let poster_url = initial?.poster_url || null;
      if (imageFile) {
        const { url, error: upErr } = await uploadFilmPoster(imageFile, form.title);
        if (upErr) return setError('Image upload failed: ' + upErr.message);
        poster_url = url;
      }
      const filmData = {
        title: form.title.trim(),
        year: form.year ? parseInt(form.year) : null,
        language: form.language.trim(),
        poster_emoji: form.poster_emoji?.trim() || '🎬',
        type: form.type || 'movie',
        genres: form.genres,
        imdb_rating: form.imdb_rating ? parseFloat(form.imdb_rating) : null,
        description: form.description.trim(),
        poster_url,
        ott_platforms: form.ott_platforms || [],
      };

      if (isEdit) {
        const { error: updErr } = await supabase.from('films').update(filmData).eq('id', initial.id);
        if (updErr) return setError('Update failed: ' + updErr.message);
        setSuccess(`"${filmData.title}" updated successfully!`);
      } else {
        const { error: insErr } = await insertFilm(filmData);
        if (insErr) return setError('Failed to add film: ' + insErr.message);
        setSuccess(`"${filmData.title}" added successfully!`);
        // Reset form and clear draft after successful add
        setForm(EMPTY_FORM);
        try { localStorage.removeItem('critiqr_film_draft'); } catch {}
        setImageFile(null); setImagePreview(null);
        if (fileRef.current) fileRef.current.value = '';
      }
      onSaved();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, maxWidth: 720 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 20, margin: 0 }}>
          {isEdit ? `✏️ Editing: ${initial?.title}` : '+ Add New Film'}
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isEdit && (
            <button
              onClick={() => {
                if (window.confirm('Clear all draft data?')) {
                  setForm(EMPTY_FORM);
                  setImageFile(null); setImagePreview(null);
                  try { localStorage.removeItem('critiqr_film_draft'); } catch {}
                  if (fileRef.current) fileRef.current.value = '';
                }
              }}
              style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text-muted)', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: "'Crimson Text', serif" }}
            >🗑 Clear Draft</button>
          )}
          {isEdit && onCancel && (
            <GoldBtn outline small onClick={onCancel}>Cancel Edit</GoldBtn>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Film Title <span style={{ color: 'var(--gold)' }}>*</span></label>
          <input value={form.title} onChange={f('title')} placeholder="e.g. Moothon"
            style={inputStyle} onFocus={focusGold} onBlur={blurBorder} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['movie', '🎥 Movie'], ['series', '📺 Series']].map(([val, label]) => (
              <button key={val} type="button" onClick={() => setForm(p => ({ ...p, type: val }))} style={{
                flex: 1, padding: '9px', border: `1.5px solid ${form.type === val ? 'var(--gold)' : 'var(--border2)'}`,
                borderRadius: 7, background: form.type === val ? 'rgba(212,175,55,0.1)' : 'var(--surface2)',
                color: form.type === val ? 'var(--gold)' : 'var(--text-dim)',
                cursor: 'pointer', fontFamily: "'Crimson Text', serif", fontSize: 15,
                fontWeight: form.type === val ? 600 : 400,
              }}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Language <span style={{ color: 'var(--gold)' }}>*</span></label>
          <input value={form.language} onChange={f('language')} placeholder="e.g. Malayalam"
            style={inputStyle} onFocus={focusGold} onBlur={blurBorder} />
        </div>
        <div>
          <label style={labelStyle}>Release Year</label>
          <input value={form.year} onChange={f('year')} placeholder="e.g. 2019" type="number"
            style={inputStyle} onFocus={focusGold} onBlur={blurBorder} />
        </div>
        <div>
          <label style={labelStyle}>IMDb Rating (out of 10)</label>
          <input value={form.imdb_rating} onChange={f('imdb_rating')} placeholder="e.g. 8.3"
            type="number" step="0.1" min="0" max="10"
            style={inputStyle} onFocus={focusGold} onBlur={blurBorder} />
        </div>
        <div>
          <label style={labelStyle}>Poster Emoji (fallback if no image)</label>
          <input value={form.poster_emoji} onChange={f('poster_emoji')} placeholder="🎬"
            style={{ ...inputStyle, fontSize: 22 }} onFocus={focusGold} onBlur={blurBorder} />
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Description</label>
        <textarea value={form.description} onChange={f('description')} placeholder="Brief plot summary…"
          style={{ ...inputStyle, height: 88, resize: 'vertical' }}
          onFocus={focusGold} onBlur={blurBorder} />
      </div>

      {/* Genre picker */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Genres <span style={{ color: 'var(--gold)' }}>*</span>{' '}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(select all that apply)</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {GENRE_OPTIONS.map(g => {
            const active = form.genres.includes(g);
            return (
              <button key={g} onClick={() => toggleGenre(g)} style={{
                background: active ? 'rgba(212,175,55,0.15)' : 'var(--surface2)',
                border: `1px solid ${active ? 'var(--gold-dim)' : 'var(--border2)'}`,
                color: active ? 'var(--gold)' : 'var(--text-dim)',
                borderRadius: 5, padding: '5px 11px', fontSize: 13,
                cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'Crimson Text', serif",
              }}>{g}</button>
            );
          })}
        </div>
      </div>


      {/* OTT Platforms */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Streaming Platforms{' '}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional — select where this film is available)</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {OTT_PLATFORMS.map(platform => {
            const active = (form.ott_platforms || []).includes(platform.id);
            return (
              <button
                key={platform.id}
                onClick={() => toggleOTT(platform.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: active ? platform.bgColor : 'var(--surface2)',
                  border: `1.5px solid ${active ? platform.color : 'var(--border2)'}`,
                  borderRadius: 8, padding: '6px 12px',
                  cursor: 'pointer', transition: 'all 0.18s',
                  fontFamily: "'Crimson Text', serif",
                  boxShadow: active ? `0 0 12px ${platform.color}33` : 'none',
                }}
              >
                <span
                  style={{ width: 18, height: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  dangerouslySetInnerHTML={{ __html: platform.logo }}
                />
                <span style={{ fontSize: 13, color: active ? platform.color : 'var(--text-dim)', fontWeight: active ? 600 : 400 }}>
                  {platform.name}
                </span>
                {active && <span style={{ color: platform.color, fontSize: 11, marginLeft: 2 }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── IMAGE UPLOAD — FIX: objectFit contain, no cropping ── */}
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>
          Poster Image{isEdit ? ' (upload new to replace)' : ''} — stored in Supabase Storage
        </label>
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: '2px dashed var(--border2)', borderRadius: 10,
            padding: imagePreview ? 8 : '32px 20px',
            textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 0.2s',
            background: 'var(--surface2)',
            minHeight: imagePreview ? 'auto' : undefined,
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold-dim)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
        >
          {imagePreview ? (
            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              {/* FIX 2: contain keeps full image visible, no cropping */}
              <img
                src={imagePreview} alt="preview"
                style={{
                  width: '100%',
                  maxHeight: 320,
                  objectFit: 'contain',   // ← was 'cover', now full image shows
                  display: 'block',
                  borderRadius: 6,
                  background: '#0a0a0f',  // dark bg so letterboxing looks clean
                }}
              />
              <div style={{
                marginTop: 8, color: 'var(--text-muted)', fontSize: 12,
              }}>Click to change image</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🖼️</div>
              <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Click to upload poster image</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>JPG, PNG, WebP — full image will be displayed</div>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleImagePick} style={{ display: 'none' }} />
        {imageFile && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'var(--green)', fontSize: 13 }}>✓ {imageFile.name}</span>
            <button
              onClick={() => { setImageFile(null); setImagePreview(initial?.poster_url || null); if (fileRef.current) fileRef.current.value = ''; }}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
            >Remove</button>
          </div>
        )}
      </div>

      {error   && <div style={{ color: 'var(--red)',   fontSize: 14, marginBottom: 14, padding: '10px 14px', background: 'rgba(192,57,43,0.1)',  borderRadius: 6, border: '1px solid rgba(192,57,43,0.2)'  }}>{error}</div>}
      {success && <div style={{ color: 'var(--green)', fontSize: 14, marginBottom: 14, padding: '10px 14px', background: 'rgba(39,174,96,0.1)',  borderRadius: 6, border: '1px solid rgba(39,174,96,0.2)'  }}>✓ {success}</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <GoldBtn onClick={handleSubmit} disabled={uploading}>
          {uploading ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Film to Database'}
        </GoldBtn>
        {isEdit && onCancel && <GoldBtn outline onClick={onCancel}>Cancel</GoldBtn>}
      </div>
    </div>
  );
}

// ── MAIN ADMIN PAGE ───────────────────────────────────────────
export default function AdminPage({ onBack, onNeedAuth, onChatClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const { profile, loading: authLoading } = useAuth();
  const [tab,     setTab]     = useState('reports');
  const [reqType,      setReqType]      = useState('movie');
  const [enriching,    setEnriching]    = useState(false);
  const [enrichLog,    setEnrichLog]    = useState([]);
  const [showEnrichLog,setShowEnrichLog]= useState(false);
  const [battleFilmA,  setBattleFilmA]  = useState('');
  const [battleFilmB,  setBattleFilmB]  = useState('');
  const [battleSaving, setBattleSaving] = useState(false);
  const [activeBattles,setActiveBattles]= useState([]);
  const [battleSuggs,  setBattleSuggs]  = useState([]);
  const [users, setUsers]     = useState([]);
  const [reports, setReports] = useState([]);
  const [bans, setBans]       = useState([]);
  const [requests, setRequests] = useState([]);
  const [films, setFilms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [banTarget, setBanTarget] = useState(null);

  // FIX 3: Edit film state — lives in AdminPage so it persists across tab switches
  const [editFilm, setEditFilm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [u, r, b, req, f, ab, bs] = await Promise.all([
      getAllUsers(), getAllReports(), getActiveBans(), getFilmRequests(), getFilms(),
      getAllActiveBattles(), getBattleSuggestions(),
    ]);
    setUsers(u); setReports(r); setBans(b); setRequests(req); setFilms(f); setActiveBattles(ab); setBattleSuggs(bs);
    setLoading(false);
  }, []);

  useEffect(() => { if (profile?.role === 'admin') load(); }, [profile, load]);

  // ── Auth guards ────────────────────────────────────────────
  if (authLoading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={40} /></div>;
  }
  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div style={{ fontSize: 52 }}>🔒</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', textAlign: 'center' }}>Login Required</h2>
        <p style={{ color: 'var(--text-dim)', textAlign: 'center', maxWidth: 360 }}>You need to be logged in to access the admin panel.</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <GoldBtn onClick={onNeedAuth}>Log In</GoldBtn>
          <GoldBtn outline onClick={onBack}>← Back to Site</GoldBtn>
        </div>
      </div>
    );
  }
  if (profile.role !== 'admin') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div style={{ fontSize: 52 }}>⛔</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', textAlign: 'center' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-dim)', textAlign: 'center', maxWidth: 400 }}>
          Logged in as <strong style={{ color: 'var(--gold)' }}>@{profile.username}</strong> — this account does not have admin privileges.
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', maxWidth: 420 }}>
          Supabase → Table Editor → profiles → find your row → set role to{' '}
          <code style={{ color: 'var(--gold)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}>admin</code> → refresh.
        </p>
        <GoldBtn outline onClick={onBack}>← Back to Site</GoldBtn>
      </div>
    );
  }

  // ── Handlers ───────────────────────────────────────────────
  const handleBan = async (target, days, reason) => {
    const until = new Date(Date.now() + days * 86400000).toISOString();
    await banUser(target.id, profile.id, reason || 'Admin action', until);
    setBanTarget(null); await load();
  };
  const handleUnban = async userId => { await unbanUser(userId); await load(); };
  const handleReportAction = async (reportId, status, reportedUserId) => {
    await updateReportStatus(reportId, status);
    if (status === 'ban') setBanTarget(users.find(u => u.id === reportedUserId));
    else await load();
  };
  const handleRequestStatus = async (id, status) => { await updateFilmRequestStatus(id, status); await load(); };
  const handleDeleteFilm = async (filmId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await deleteFilm(filmId); await load();
  };
  const handleFilmSaved = () => { setEditFilm(null); load(); };

  const pendingReports  = reports.filter(r => r.status === 'pending').length;
  const pendingRequests = requests.filter(r => r.status === 'pending').length;

  const tabs = [
    { id: 'reports',  label: '⚑ Reports',     badge: pendingReports },
    { id: 'users',    label: '👥 Users' },
    { id: 'bans',     label: '🚫 Bans',        badge: bans.length },
    { id: 'add_film', label: '＋ Add Film' },
    { id: 'films',    label: '🎬 Films',        badge: films.length },
    { id: 'requests', label: '📬 Requests',     badge: pendingRequests },
    { id: 'battles',  label: '⚔️ Battles' },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header */}
      <Navbar onBack={onBack} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px 60px' }}>
        {/* Stats */}
        {!loading && (
          <div className="fade-up" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 28 }}>
            <StatCard icon="👥" label="Users"          value={users.length} />
            <StatCard icon="⚑" label="Pending Reports" value={pendingReports}  accent={pendingReports > 0} />
            <StatCard icon="🚫" label="Active Bans"    value={bans.length} />
            <StatCard icon="🎬" label="Total Films"    value={films.length} accent />
            <StatCard icon="📬" label="Film Requests"  value={pendingRequests}  accent={pendingRequests > 0} />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 24, background: 'var(--surface)', borderRadius: 10, padding: 4, border: '1px solid var(--border)', overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setEditFilm(null); }} style={{
              flex: '1 0 auto', padding: '9px 14px', border: 'none', borderRadius: 7,
              background: tab === t.id ? 'var(--surface2)' : 'transparent',
              color: tab === t.id ? 'var(--gold)' : 'var(--text-dim)',
              fontSize: 13, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
              fontWeight: tab === t.id ? 600 : 400,
              borderBottom: tab === t.id ? '1px solid var(--gold-dim)' : '1px solid transparent',
              whiteSpace: 'nowrap', transition: 'all 0.2s',
            }}>
              {t.label}
              {t.badge > 0 && <span style={{ marginLeft: 5, background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 5px', fontWeight: 700 }}>{t.badge}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
        ) : (
          <>
            {/* ── ADD FILM TAB ── */}
            {tab === 'add_film' && (
              // FIX 1: key="add" keeps this instance stable — switching tabs and back won't reset the form
              <FilmForm key="add" onSaved={handleFilmSaved} />
            )}

            {/* ── FILMS TAB (with Edit button) ── */}
            {tab === 'films' && (
              <>
                {/* ── OMDB Enrichment Tool ── */}
                <div style={{ background: 'var(--surface)', border: '1px solid rgba(212,175,55,0.2)',
                  borderRadius: 12, padding: 18, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 16, marginBottom: 4 }}>
                        🎬 Auto-Enrich Films from OMDb
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        Fetches director, cast, runtime, poster & IMDb rating for all films missing this data.
                      </div>
                    </div>
                    <GoldBtn
                      disabled={enriching}
                      onClick={async () => {
                        setEnriching(true);
                        setEnrichLog([]);
                        setShowEnrichLog(true);
                        const toEnrich = films; // enrich all films
                        let updated = 0, failed = 0;
                        for (const film of toEnrich) {
                          setEnrichLog(prev => [...prev, { film: film.title, status: 'searching' }]);
                          const data = await getOMDBByTitle(film.title, film.year);
                          if (data && data.imdbID) {
                            const meta = {
                              imdb_id:     data.imdbID,
                              director:    data.Director !== 'N/A' ? data.Director : null,
                              runtime:     data.Runtime  !== 'N/A' ? data.Runtime  : null,
                              country:     data.Country  !== 'N/A' ? data.Country  : null,
                              awards:      data.Awards   !== 'N/A' ? data.Awards   : null,
                              film_cast:   (data.Actors && data.Actors !== 'N/A') ? data.Actors.split(',').map(a => a.trim()) : null,
                              poster_url:  (!film.poster_url && data.Poster !== 'N/A') ? data.Poster : film.poster_url,
                              description: (!film.description && data.Plot !== 'N/A') ? data.Plot : film.description,
                              imdb_rating: (!film.imdb_rating && data.imdbRating !== 'N/A') ? parseFloat(data.imdbRating) : film.imdb_rating,
                            };
                            await updateFilmMeta(film.id, meta);
                            updated++;
                            setEnrichLog(prev => prev.map(l => l.film === film.title ? { ...l, status: 'done' } : l));
                          } else {
                            failed++;
                            setEnrichLog(prev => prev.map(l => l.film === film.title ? { ...l, status: 'not found' } : l));
                          }
                          // Small delay to avoid rate limiting
                          await new Promise(r => setTimeout(r, 300));
                        }
                        await load();
                        setEnriching(false);
                        setEnrichLog(prev => [...prev, { film: `✓ Done! ${updated} updated, ${failed} not found.`, status: 'summary' }]);
                      }}
                    >
                      {enriching ? `Enriching… (${enrichLog.filter(l=>l.status==='done'||l.status==='not found').length}/${films.length})` : '✨ Enrich All Films'}
                    </GoldBtn>
                  </div>

                  {showEnrichLog && enrichLog.length > 0 && (
                    <div style={{ marginTop: 14, background: 'var(--surface2)', borderRadius: 8,
                      padding: '12px 14px', maxHeight: 200, overflowY: 'auto',
                      fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                      {enrichLog.map((l, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 4,
                          color: l.status === 'done' ? 'var(--green)' : l.status === 'not found' ? 'var(--red)' :
                            l.status === 'summary' ? 'var(--gold)' : 'var(--text-muted)' }}>
                          <span>{l.status === 'done' ? '✓' : l.status === 'not found' ? '✗' : l.status === 'summary' ? '★' : '…'}</span>
                          <span>{l.film}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Edit form appears inline above the grid when editing */}
                {editFilm && (
                  <div style={{ marginBottom: 24 }}>
                    <FilmForm
                      key={`edit-${editFilm.id}`}
                      initial={{
                        ...editFilm,
                        year: editFilm.year?.toString() || '',
                        imdb_rating: editFilm.imdb_rating?.toString() || '',
                        genres: editFilm.genres || [],
                        type: editFilm.type || 'movie',
                      }}
                      isEdit
                      onSaved={handleFilmSaved}
                      onCancel={() => setEditFilm(null)}
                    />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
                  {films.map(f => (
                    <div key={f.id} style={{ background: 'var(--surface)', border: `1px solid ${editFilm?.id === f.id ? 'var(--gold-dim)' : 'var(--border)'}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      {/* Poster thumbnail — contain so image isn't cropped */}
                      <div style={{ width: '100%', height: 140, background: '#0a0a0f', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {f.poster_url ? (
                          <img src={f.poster_url} alt={f.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ fontSize: 40 }}>{f.poster_emoji}</span>
                        )}
                      </div>
                      <div style={{ padding: '10px 14px 14px' }}>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, color: 'var(--text)', fontWeight: 700, marginBottom: 2, lineHeight: 1.25 }}>{f.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{f.year} · {f.language}</div>
                        {/* Quick type dropdown */}
                        <select
                          value={f.type || 'movie'}
                          onChange={async e => {
                            await supabase.from('films').update({ type: e.target.value }).eq('id', f.id);
                            await load();
                          }}
                          style={{
                            width: '100%', marginBottom: 8,
                            background: 'var(--surface2)', border: '1px solid var(--border2)',
                            borderRadius: 5, color: f.type === 'series' ? '#3B82F6' : 'var(--gold-dim)',
                            padding: '4px 8px', fontSize: 12,
                            fontFamily: "'Crimson Text', serif", cursor: 'pointer', outline: 'none',
                          }}
                        >
                          <option value="movie">🎥 Movie</option>
                          <option value="series">📺 Series</option>
                        </select>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {/* ── EDIT BUTTON ── */}
                          <button
                            onClick={() => { setEditFilm(f); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            style={{
                              background: editFilm?.id === f.id ? 'rgba(212,175,55,0.15)' : 'none',
                              border: '1px solid var(--gold-dim)', color: 'var(--gold)',
                              borderRadius: 4, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
                              fontFamily: "'Crimson Text', serif", transition: 'background 0.15s',
                            }}
                          >✏️ Edit</button>
                          <button
                            onClick={() => handleDeleteFilm(f.id, f.title)}
                            style={{
                              background: 'none', border: '1px solid rgba(192,57,43,0.3)', color: 'var(--red)',
                              borderRadius: 4, padding: '3px 9px', fontSize: 11, cursor: 'pointer',
                              fontFamily: "'Crimson Text', serif",
                            }}
                          >🗑 Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── REPORTS TAB ── */}
            {tab === 'reports' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reports.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 60 }}>No reports yet. 🎉</div>}
                {reports.map(r => (
                  <div key={r.id} style={{ background: 'var(--surface)', border: `1px solid ${r.status === 'pending' ? 'rgba(192,57,43,0.25)' : 'var(--border)'}`, borderRadius: 12, padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      <div>
                        <span style={{ color: '#C0392B', fontWeight: 700, fontFamily: "'Playfair Display', serif", fontSize: 15 }}>@{r.reported?.username}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}> reported in </span>
                        <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>{r.films?.title}</span>
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 4, fontFamily: "'DM Mono', monospace",
                        background: r.status === 'pending' ? 'rgba(192,57,43,0.1)' : r.status === 'resolved' ? 'rgba(39,174,96,0.1)' : 'var(--surface2)',
                        color: r.status === 'pending' ? '#C0392B' : r.status === 'resolved' ? 'var(--green)' : 'var(--text-muted)',
                        border: '1px solid currentColor',
                      }}>{r.status}</span>
                    </div>
                    <p style={{ color: 'var(--text-dim)', fontSize: 14, margin: '0 0 6px' }}><strong>Reason:</strong> {r.reason}</p>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: r.status === 'pending' ? 12 : 0 }}>
                      By @{r.reporter?.username} · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <GoldBtn danger small onClick={() => setBanTarget(users.find(u => u.id === r.reported_user_id))}>Ban User</GoldBtn>
                        <GoldBtn small onClick={() => handleReportAction(r.id, 'resolved')}>Mark Resolved</GoldBtn>
                        <GoldBtn outline small onClick={() => handleReportAction(r.id, 'dismissed')}>Dismiss</GoldBtn>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── USERS TAB ── */}
            {tab === 'users' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {users.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>No users yet.</div>}
                {users.map(u => {
                  const ban = bans.find(b => b.user_id === u.id);
                  return (
                    <div key={u.id} style={{ background: 'var(--surface)', border: `1px solid ${ban ? 'rgba(192,57,43,0.2)' : 'var(--border)'}`, borderRadius: 10, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: u.role === 'admin' ? 'linear-gradient(135deg,var(--gold-dim),var(--gold))' : 'var(--surface2)', border: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: u.role === 'admin' ? 'var(--black)' : 'var(--text-dim)', fontWeight: 700 }}>{u.username?.[0]?.toUpperCase()}</div>
                        <div>
                          <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15 }}>
                            @{u.username}
                            {u.role === 'admin' && <span style={{ color: 'var(--gold)', fontSize: 11, marginLeft: 6 }}>★ admin</span>}
                            {ban && <span style={{ color: '#C0392B', fontSize: 11, marginLeft: 6 }}>🚫 banned until {new Date(ban.ban_until).toLocaleDateString()}</span>}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Joined {new Date(u.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      {u.role !== 'admin' && (
                        ban
                          ? <GoldBtn small outline onClick={() => handleUnban(u.id)}>Unban</GoldBtn>
                          : <GoldBtn small danger onClick={() => setBanTarget(u)}>Ban</GoldBtn>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── BANS TAB ── */}
            {tab === 'bans' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bans.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>No active bans.</div>}
                {bans.map(b => (
                  <div key={b.id} style={{ background: 'var(--surface)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 10, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ color: 'var(--text)', fontWeight: 600 }}>@{b.profiles?.username}</div>
                      <div style={{ color: '#C0392B', fontSize: 13, marginTop: 2 }}>Until: {new Date(b.ban_until).getFullYear() > 2100 ? 'Permanent' : new Date(b.ban_until).toLocaleDateString()}</div>
                      {b.reason && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Reason: {b.reason}</div>}
                    </div>
                    <GoldBtn small outline onClick={() => handleUnban(b.user_id)}>Lift Ban</GoldBtn>
                  </div>
                ))}
              </div>
            )}

            {/* ── REQUESTS TAB ── */}
            {tab === 'requests' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                {/* Movie / Series sub-tabs */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                  {[['movie', '🎥 Movie Requests'], ['series', '📺 Series Requests']].map(([val, label]) => {
                    const count = requests.filter(r => (r.type || 'movie') === val && r.status === 'pending').length;
                    return (
                      <button key={val} onClick={() => setReqType(val)} style={{
                        padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer',
                        borderBottom: reqType === val ? '2px solid var(--gold)' : '2px solid transparent',
                        color: reqType === val ? 'var(--gold)' : 'var(--text-muted)',
                        fontSize: 14, fontFamily: "'Crimson Text', serif",
                        fontWeight: reqType === val ? 600 : 400,
                      }}>
                        {label}
                        {count > 0 && (
                          <span style={{ marginLeft: 6, background: 'rgba(212,175,55,0.15)', color: 'var(--gold)', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {requests.filter(r => (r.type || 'movie') === reqType).length === 0 && (
                  <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>
                    No {reqType} requests yet.
                  </div>
                )}
                {requests.filter(r => (r.type || 'movie') === reqType).map(req => {
                  const email = req.requester_email;
                  const year  = req.release_year ? ` (${req.release_year})` : '';

                  // ── Gmail compose URLs — opens Gmail in a new tab, no default mail app needed ──
                  const gmailURL = (body) => email
                    ? 'https://mail.google.com/mail/?view=cm&fs=1'
                      + '&to=' + encodeURIComponent(email)
                      + '&su=' + encodeURIComponent('Re: Your Critiqr film request — "' + req.film_name + '"')
                      + '&body=' + encodeURIComponent(body)
                    : null;

                  const mailtoAdded    = gmailURL('Hi there,\n\nGreat news! The film you requested — "' + req.film_name + '"' + year + ' — has been added to Critiqr.\n\nYou can now find it and leave a rating at our website.\n\nThank you for helping us grow the collection!\n\n— The Critiqr Team');
                  const mailtoDeclined = gmailURL('Hi there,\n\nThank you for requesting "' + req.film_name + '"' + year + ' on Critiqr.\n\nUnfortunately we are unable to add this film at this time. This could be due to licensing, availability of information, or other reasons.\n\nWe appreciate your interest and hope you enjoy the rest of our collection!\n\n— The Critiqr Team');
                  const mailtoCustom   = gmailURL('Hi there,\n\nRegarding your request for "' + req.film_name + '"' + year + ' on Critiqr:\n\n\n\n— The Critiqr Team');

                  return (
                    <div key={req.id} style={{
                      background: 'var(--surface)',
                      border: `1px solid ${req.status === 'pending' ? 'rgba(212,175,55,0.18)' : 'var(--border)'}`,
                      borderRadius: 10, padding: '16px 20px',
                    }}>
                      {/* Top row: title + status badge */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                        <div>
                          <span style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 17, fontWeight: 700 }}>
                            {req.film_name}
                          </span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 10 }}>
                            {req.language}{req.release_year ? ` · ${req.release_year}` : ''}
                          </span>
                          {req.type && (
                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4,
                              background: req.type === 'series' ? 'rgba(59,130,246,0.12)' : 'rgba(212,175,55,0.08)',
                              color: req.type === 'series' ? '#60a5fa' : 'var(--gold-dim)',
                              fontFamily: "'DM Mono', monospace", marginLeft: 6,
                            }}>{req.type === 'series' ? '📺 Series' : '🎥 Movie'}</span>
                          )}
                        </div>
                        <span style={{
                          fontSize: 11, padding: '3px 9px', borderRadius: 4,
                          fontFamily: "'DM Mono', monospace",
                          background: req.status === 'pending' ? 'rgba(212,175,55,0.1)' : req.status === 'added' ? 'rgba(39,174,96,0.1)' : 'var(--surface2)',
                          color: req.status === 'pending' ? 'var(--gold)' : req.status === 'added' ? 'var(--green)' : 'var(--text-muted)',
                        }}>{req.status}</span>
                      </div>

                      {/* Date + email address (clickable mailto) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {email ? (
                          <a href={mailtoCustom} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            color: 'var(--gold-dim)', fontSize: 12,
                            background: 'rgba(212,175,55,0.07)',
                            border: '1px solid rgba(212,175,55,0.2)',
                            borderRadius: 5, padding: '2px 9px',
                            textDecoration: 'none', fontFamily: "'DM Mono', monospace",
                            transition: 'background 0.15s, color 0.15s',
                          }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.15)'; e.currentTarget.style.color = 'var(--gold)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.07)'; e.currentTarget.style.color = 'var(--gold-dim)'; }}
                            title="Click to open your email app and reply"
                          >
                            ✉ {email}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>No email provided</span>
                        )}
                        {req.profiles?.username && (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>by @{req.profiles.username}</span>
                        )}
                      </div>

                      {/* Action buttons */}
                      {req.status === 'pending' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {/* Mark Added + optionally email user */}
                          <GoldBtn small onClick={() => {
                            if (mailtoAdded) window.open(mailtoAdded, '_blank');
                            handleRequestStatus(req.id, 'added');
                          }}>
                            ✓ Mark Added{email ? ' & Email User' : ''}
                          </GoldBtn>

                          {/* Decline + optionally email user */}
                          <GoldBtn small outline onClick={() => {
                            if (mailtoDeclined) window.open(mailtoDeclined, '_blank');
                            handleRequestStatus(req.id, 'declined');
                          }}>
                            ✕ Decline{email ? ' & Email User' : ''}
                          </GoldBtn>

                          {/* Just email without changing status */}
                          {email && (
                            <a href={mailtoCustom} style={{
                              display: 'inline-flex', alignItems: 'center',
                              background: 'none', border: '1px solid var(--border2)',
                              color: 'var(--text-dim)', borderRadius: 6,
                              padding: '5px 12px', fontSize: 13, cursor: 'pointer',
                              textDecoration: 'none', fontFamily: "'Crimson Text', serif",
                              transition: 'border-color 0.15s, color 0.15s',
                            }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-dim)'; e.currentTarget.style.color = 'var(--gold)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text-dim)'; }}
                            >✉ Just Email</a>
                          )}
                        </div>
                      )}

                      {/* Completed requests — still show email button */}
                      {req.status !== 'pending' && email && (
                        <a href={mailtoCustom} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: 'none', border: '1px solid var(--border2)',
                          color: 'var(--text-muted)', borderRadius: 6,
                          padding: '4px 11px', fontSize: 12, cursor: 'pointer',
                          textDecoration: 'none', fontFamily: "'Crimson Text', serif",
                        }}>✉ Reply to user</a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          {/* ── BATTLES TAB ── */}
          {tab === 'battles' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Create battle */}
              <div style={{ background: 'var(--surface)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 18, marginBottom: 6 }}>⚔️ Create Today's Battle</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                  Pick two films. The battle will appear on the Battle page immediately.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Film A</label>
                    <select value={battleFilmA} onChange={e => setBattleFilmA(e.target.value)} style={inputStyle}>
                      <option value="">Select…</option>
                      {films.filter(f => f.id !== parseInt(battleFilmB)).map(f => (
                        <option key={f.id} value={f.id}>{f.title} ({f.language})</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 20, fontWeight: 900, textAlign: 'center', paddingTop: 20 }}>VS</div>
                  <div>
                    <label style={labelStyle}>Film B</label>
                    <select value={battleFilmB} onChange={e => setBattleFilmB(e.target.value)} style={inputStyle}>
                      <option value="">Select…</option>
                      {films.filter(f => f.id !== parseInt(battleFilmA)).map(f => (
                        <option key={f.id} value={f.id}>{f.title} ({f.language})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <GoldBtn disabled={!battleFilmA || !battleFilmB || battleSaving}
                  onClick={async () => {
                    setBattleSaving(true);
                    await createAdminBattle(parseInt(battleFilmA), parseInt(battleFilmB), profile?.id);
                    setBattleFilmA(''); setBattleFilmB('');
                    await load();
                    setBattleSaving(false);
                  }}>
                  {battleSaving ? 'Creating…' : '⚔️ Create Battle'}
                </GoldBtn>
              </div>

              {/* Active battles list */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 16, marginBottom: 14 }}>
                  Active Battles ({activeBattles.length})
                </h3>
                {activeBattles.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No active battles yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {activeBattles.map(b => {
                      const votes = b.battle_votes || [];
                      return (
                        <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: 'var(--surface2)', borderRadius: 8, padding: '10px 14px', flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{b.film_a?.title}</span>
                            <span style={{ color: 'var(--gold)', margin: '0 8px', fontWeight: 700 }}>VS</span>
                            <span style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{b.film_b?.title}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 10 }}>{votes.length} votes</span>
                          </div>
                          <GoldBtn small outline onClick={async () => { await toggleBattle(b.id, false); load(); }}>
                            Deactivate
                          </GoldBtn>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Suggestions from users */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 16, marginBottom: 14 }}>
                  💡 User Suggestions ({battleSuggs.length})
                </h3>
                {battleSuggs.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>No suggestions yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {battleSuggs.map(s => (
                      <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        background: 'var(--surface2)', borderRadius: 8, padding: '10px 14px', gap: 10 }}>
                        <div>
                          <div style={{ color: 'var(--text)', fontSize: 14 }}>{s.suggestion}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 3 }}>by @{s.profiles?.username}</div>
                        </div>
                        <button onClick={async () => { const { deleteBattleSuggestion } = await import('../lib/supabase'); await deleteBattleSuggestion(s.id); load(); }}
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 15 }}>🗑</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

            )}
          </>
        )}
      </div>

      <BanModal target={banTarget} onClose={() => setBanTarget(null)} onBan={handleBan} />
    </div>
  );
}
